import { CURRENCY_CODE } from 'constants/index';
import { UserDataInterface } from 'interface/reducers/auth';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { NSConsignment } from '../Consignment.d';
import {
  CountriesStateInterface,
  ProvinceStateInterface,
} from 'interface/reducers/postalCode';

interface IUseModalSelectLocationProps
  extends NSConsignment.IModalSelectLocationActionProps {
  userData: UserDataInterface;
  countries: CountriesStateInterface;
  provinces: ProvinceStateInterface;
}

interface ISelectValues {
  label: string;
  value: string | number;
}

export const useModalSelectLocation = (props: IUseModalSelectLocationProps) => {
  const {
    userData,
    countries,
    provinces,
    fetchCountries,
    fetchProvinces,
    updateProfile,
    updateUserCountry,
    fetchCurrencyRate,
    refreshToken,
    fetchProfile,
  } = props;
  const history = useHistory();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [locationSubmitted, setLocationSubmitted] = useState(false);
  const [errorLocation, setErrorLocation] = useState<any>(null);
  const [countryList, setCountryList] = useState([
    { value: '', label: 'Country*' },
  ]);
  const [provinceList, setProvinceList] = useState([
    { value: 0, label: 'Province*' },
  ]);
  const [country, setCountry] = useState(null);
  const [province, setProvince] = useState(null);
  const [countrySelected, setCountrySelected] =
    useState<ISelectValues | null>(null);
  const [provinceSelected, setProvinceSelected] =
    useState<ISelectValues | null>(null);
  const sellerLocation =
    userData &&
    userData.user_setting &&
    userData.user_setting.seller_location_id;
  const sellerCountry = countries.data
    ? countries.data.find((c) => c.code3 === userData.country_code)
    : null;
  const sellerProvince =
    userData && userData.user_setting && userData.user_setting.seller_location;
  const sellerLocationArr = [sellerProvince?.name, sellerCountry?.name];
  const sellerLocationDisplaying = sellerLocationArr
    .filter((item) => item)
    .join(', ');

  const toggleModalSellerLocation = () => {
    const sellerLocation =
      userData &&
      userData.user_setting &&
      userData.user_setting.seller_location_id;

    setIsOpenModal(!isOpenModal);
    setLocationSubmitted(false);
    setErrorLocation(null);
    if (!sellerLocation) history.replace('/user_dashboard/profile-settings');
  };

  const handleCountryChange = (selected: any) => {
    setCountry(!selected ? null : selected.label);
    setCountrySelected(!selected ? null : selected);
    setProvince(null);
    setProvinceSelected(null);
    setErrorLocation(null);
    if (selected) {
      fetchProvinces(selected.value);
    }
  };

  const handleProvinceChange = (selected: any) => {
    setProvince(!selected ? null : selected.label);
    setProvinceSelected(!selected ? null : selected);
    setErrorLocation(null);
  };

  const validateForm = () => {
    const errors: any = {};
    if (!countrySelected) {
      errors['countrySelected'] = ['Please select your country location.'];
    }
    if (!provinceSelected) {
      errors['provinceSelected'] = ['Please select your province location.'];
    }
    if (Object.keys(errors).length) {
      setErrorLocation({
        errors,
        status_code: 422,
        message: 'Please fill the form',
      });
    }
    return errors;
  };

  const onSubmitLocation = () => {
    if (!Object.keys(validateForm()).length) {
      const payload = {
        email: userData.email,
        first_name: userData.first_name,
        family_name: userData.family_name,
        sex: userData.sex,
        birthed_at: userData.birthed_at,
        seller_location_id: provinceSelected?.value,
      };
      const data = {
        country_code: countrySelected?.value,
      };

      updateProfile(payload).then(() => {
        updateUserCountry(data).then(() => {
          const currencyCode =
            CURRENCY_CODE[countrySelected?.value === 'IDN' ? 'IDN' : 'ETC'];
          fetchCurrencyRate(currencyCode);
          fetchProfile();
          if (
            countrySelected?.value === 'MYS' ||
            countrySelected?.value === 'SGP'
          ) {
            history.push('/user_dashboard/profile-settings');
          }
        });
        setLocationSubmitted(false);
        setIsOpenModal(false);
        refreshToken();
      });
    }
  };

  useEffect(() => {
    if (!countries.isFetch && !userData.isFetch) {
      if (countries.data.length && countries.status_code === 200) {
        const base = { value: '', label: 'Country*' };
        const data = countries.data.filter((c) => c.code3 === 'IDN');
        const country = data.map((country) => ({
          value: country.code3,
          label: country.name,
        }));
        const countryLocation = userData && userData.country_code;
        try {
          const currencyCode =
            CURRENCY_CODE[countryLocation === 'IDN' ? 'IDN' : 'ETC'];
          fetchCurrencyRate(currencyCode);
        } catch (error) {
          fetchCurrencyRate(CURRENCY_CODE['IDN']);
        }
        const getCountry = country.find((c) => c.value == countryLocation);
        if (!countrySelected) {
          if (getCountry) {
            setCountrySelected(getCountry);
          }
        }
        setCountryList([base, ...country]);
        if (getCountry) {
          fetchProvinces(getCountry.value);
        }
      }
    }
  }, [countries.isFetch, userData.isFetch]);

  useEffect(() => {
    if (provinces.data.length && provinces.status_code === 200) {
      const def = { value: 0, label: 'Provinsi*' };
      const { data } = provinces;
      const province = data.map((province, index) => ({
        value: province.id,
        label: province.name,
      }));
      const countryLocation = userData && userData.country_code;
      const sellerLocation =
        userData &&
        userData.user_setting &&
        userData.user_setting.seller_location_id;
      const getProvince = province.find((prov) => prov.value == sellerLocation);
      if (provinceSelected == null) {
        if (getProvince) {
          setProvinceSelected(getProvince);
        }
      }
      setProvinceList([def, ...province]);
      if (
        !sellerLocation ||
        (countrySelected?.value !== countryLocation &&
          getProvince?.value !== sellerLocation)
      ) {
        setIsOpenModal(true);
      }
    }
  }, [provinces.isFetch]);

  useEffect(() => {
    fetchCountries();
  }, []);

  return {
    isOpenModal,
    locationSubmitted,
    errorLocation,
    countryList,
    provinceList,
    country,
    province,
    countrySelected,
    provinceSelected,
    sellerLocation,
    sellerLocationDisplaying,
    setIsOpenModal,
    setLocationSubmitted,
    setErrorLocation,
    setCountryList,
    setProvinceList,
    setCountry,
    setProvince,
    setCountrySelected,
    setProvinceSelected,
    toggleModalSellerLocation,
    handleCountryChange,
    handleProvinceChange,
    validateForm,
    onSubmitLocation,
  };
};
